import { useSyncExternalStore } from 'react';
import { projectContextStore } from './projectContext.js';
import { useFloatingWindowStore, type FloatingWindowState } from './floatingWindow.store.js';
import { useR2ChatStore, type ChatContext, type ChatMessage, type ContextSummary } from './r2ChatStore.js';
import { saveProjectSettings } from '../lib/localProjectPersistence.js';

export interface ProjectMemoryViewport {
  zoom?: number;
  panX?: number;
  panY?: number;
  page?: number;
  rotation?: number;
}

export interface ProjectMemorySnapshot {
  version: number;
  userId: string;
  projectId: string;
  updatedAt: string;
  lastActiveProjectId: string | null;
  lastActiveRoute: string;
  navigationHistory: string[];
  lastActiveDocumentId: string | null;
  lastActiveDocumentName: string | null;
  lastActiveDrawingId: string | null;
  lastActiveSheetId: string | null;
  lastSelectedTab: string | null;
  activePanels: string[];
  recentFiles: Array<{ id: string; name: string; path?: string; type?: string; openedAt: string }>;
  openFloatingWindows: Record<string, FloatingWindowState>;
  viewportByDocument: Record<string, ProjectMemoryViewport>;
  activeFilters: Record<string, unknown>;
  searchHistory: string[];
  selectedObjects: Array<Record<string, unknown>>;
  expandedTreeNodes: string[];
  scrollPositions: Record<string, number>;
  undoRedoSession: Record<string, unknown> | null;
  ai: {
    activeWorkspace: string | null;
    sessionId: string | null;
    context: ChatContext;
    contextSummary: ContextSummary | null;
    messages: ChatMessage[];
    prompts: string[];
    references: Array<Record<string, unknown>>;
  };
}

interface ProjectMemoryState {
  hydrated: boolean;
  scopeKey: string;
  snapshot: ProjectMemorySnapshot;
  hydrate: (userId?: string | null, projectId?: string | null) => ProjectMemorySnapshot;
  setActiveProject: (projectId: string | null) => void;
  recordRoute: (route: string) => void;
  recordDocument: (doc: { id: string; name?: string | null; type?: string; path?: string | null; drawingId?: string | null; sheetId?: string | null }) => void;
  recordTab: (tabId: string | null) => void;
  setPanelActive: (panelId: string, active: boolean) => void;
  recordViewport: (documentId: string, viewport: ProjectMemoryViewport) => void;
  recordFilter: (scope: string, value: unknown) => void;
  recordSearch: (query: string) => void;
  recordSelectedObject: (obj: Record<string, unknown> | null) => void;
  setTreeNodeExpanded: (nodeId: string, expanded: boolean) => void;
  recordScroll: (scope: string, scrollTop: number) => void;
  recordUndoRedoSession: (session: Record<string, unknown> | null) => void;
  recordAiWorkspace: (workspace: string | null) => void;
  recordAiPrompt: (prompt: string) => void;
  recordAiReference: (reference: Record<string, unknown>) => void;
  syncFloatingWindows: () => void;
  syncAiContext: () => void;
  flush: () => void;
  getProjectNumberMemory: () => string | null;
}

const VERSION = 1;
const STORAGE_PREFIX = 'r2.project-memory.v1';
const GLOBAL_LAST_KEY = 'r2.project-memory.lastScope';
const subs = new Set<() => void>();
let saveTimer: number | null = null;
let restoringFloatingWindowsUntil = 0;

function canUseStorage(): boolean {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
}

function safeScope(value?: string | null): string {
  return (value || 'guest').replace(/[^a-zA-Z0-9._@-]/g, '_');
}

function currentUserId(): string {
  if (!canUseStorage()) return 'guest';
  return localStorage.getItem('r2:lastLoginEmail') || 'guest';
}

function memoryKey(userId: string, projectId: string): string {
  return `${STORAGE_PREFIX}:${safeScope(userId)}:${safeScope(projectId)}`;
}

function defaultProjectId(projectId?: string | null): string {
  return projectId || projectContextStore.getState().activeProjectId || 'workspace';
}

function createSnapshot(userId: string, projectId: string): ProjectMemorySnapshot {
  return {
    version: VERSION,
    userId,
    projectId,
    updatedAt: new Date().toISOString(),
    lastActiveProjectId: projectId === 'workspace' ? null : projectId,
    lastActiveRoute: typeof window !== 'undefined' ? (window.location.hash.slice(1).split('?')[0] || 'dashboard') : 'dashboard',
    navigationHistory: [],
    lastActiveDocumentId: null,
    lastActiveDocumentName: null,
    lastActiveDrawingId: null,
    lastActiveSheetId: null,
    lastSelectedTab: null,
    activePanels: [],
    recentFiles: [],
    openFloatingWindows: {},
    viewportByDocument: {},
    activeFilters: {},
    searchHistory: [],
    selectedObjects: [],
    expandedTreeNodes: [],
    scrollPositions: {},
    undoRedoSession: null,
    ai: {
      activeWorkspace: null,
      sessionId: null,
      context: {},
      contextSummary: null,
      messages: [],
      prompts: [],
      references: [],
    },
  };
}

function sanitize(raw: unknown, userId: string, projectId: string): ProjectMemorySnapshot {
  const base = createSnapshot(userId, projectId);
  if (!raw || typeof raw !== 'object') return base;
  const r = raw as Partial<ProjectMemorySnapshot>;
  return {
    ...base,
    ...r,
    version: VERSION,
    userId,
    projectId,
    lastActiveProjectId: typeof r.lastActiveProjectId === 'string' ? r.lastActiveProjectId : base.lastActiveProjectId,
    lastActiveRoute: typeof r.lastActiveRoute === 'string' ? r.lastActiveRoute : base.lastActiveRoute,
    navigationHistory: Array.isArray(r.navigationHistory) ? r.navigationHistory.filter((x): x is string => typeof x === 'string').slice(0, 50) : [],
    activePanels: Array.isArray(r.activePanels) ? r.activePanels.filter((x): x is string => typeof x === 'string') : [],
    recentFiles: Array.isArray(r.recentFiles) ? r.recentFiles.slice(0, 30) as ProjectMemorySnapshot['recentFiles'] : [],
    openFloatingWindows: r.openFloatingWindows && typeof r.openFloatingWindows === 'object' ? r.openFloatingWindows as Record<string, FloatingWindowState> : {},
    viewportByDocument: r.viewportByDocument && typeof r.viewportByDocument === 'object' ? r.viewportByDocument as Record<string, ProjectMemoryViewport> : {},
    activeFilters: r.activeFilters && typeof r.activeFilters === 'object' ? r.activeFilters as Record<string, unknown> : {},
    searchHistory: Array.isArray(r.searchHistory) ? r.searchHistory.filter((x): x is string => typeof x === 'string').slice(0, 30) : [],
    selectedObjects: Array.isArray(r.selectedObjects) ? r.selectedObjects.slice(0, 50) as Array<Record<string, unknown>> : [],
    expandedTreeNodes: Array.isArray(r.expandedTreeNodes) ? r.expandedTreeNodes.filter((x): x is string => typeof x === 'string').slice(0, 500) : [],
    scrollPositions: r.scrollPositions && typeof r.scrollPositions === 'object' ? r.scrollPositions as Record<string, number> : {},
    ai: {
      ...base.ai,
      ...(r.ai && typeof r.ai === 'object' ? r.ai : {}),
      messages: Array.isArray(r.ai?.messages) ? r.ai.messages.slice(-80) : [],
      prompts: Array.isArray(r.ai?.prompts) ? r.ai.prompts.filter((x): x is string => typeof x === 'string').slice(0, 50) : [],
      references: Array.isArray(r.ai?.references) ? r.ai.references.slice(0, 50) as Array<Record<string, unknown>> : [],
    },
  };
}

function readSnapshot(userId: string, projectId: string): ProjectMemorySnapshot {
  if (!canUseStorage()) return createSnapshot(userId, projectId);
  try {
    const raw = localStorage.getItem(memoryKey(userId, projectId));
    return sanitize(raw ? JSON.parse(raw) : null, userId, projectId);
  } catch {
    return createSnapshot(userId, projectId);
  }
}

function writeSnapshot(snapshot: ProjectMemorySnapshot): void {
  if (!canUseStorage()) return;
  try {
    localStorage.setItem(memoryKey(snapshot.userId, snapshot.projectId), JSON.stringify(snapshot));
    localStorage.setItem(GLOBAL_LAST_KEY, JSON.stringify({ userId: snapshot.userId, projectId: snapshot.projectId, savedAt: snapshot.updatedAt }));
  } catch { /* quota fallback */ }
}

function notify(): void { subs.forEach((fn) => fn()); }

function debouncedPersist(snapshot: ProjectMemorySnapshot): void {
  if (saveTimer) window.clearTimeout(saveTimer);
  saveTimer = window.setTimeout(() => {
    writeSnapshot(snapshot);
    if (snapshot.lastActiveProjectId) {
      void saveProjectSettings(snapshot.lastActiveProjectId, { projectMemory: snapshot }).catch(() => undefined);
    }
  }, 450);
}

let state: ProjectMemoryState;

function update(mutator: (snapshot: ProjectMemorySnapshot) => ProjectMemorySnapshot): void {
  const next = mutator(state.snapshot);
  state = { ...state, snapshot: { ...next, updatedAt: new Date().toISOString() } };
  debouncedPersist(state.snapshot);
  notify();
}

function makeState(): ProjectMemoryState {
  const userId = currentUserId();
  const projectId = defaultProjectId();
  const snapshot = readSnapshot(userId, projectId);
  return {
    hydrated: true,
    scopeKey: memoryKey(userId, projectId),
    snapshot,
    hydrate(userIdInput, projectIdInput) {
      const nextUser = userIdInput || currentUserId();
      const nextProject = defaultProjectId(projectIdInput);
      if (state) writeSnapshot(state.snapshot);
      const nextSnapshot = readSnapshot(nextUser, nextProject);
      state = { ...state, hydrated: true, scopeKey: memoryKey(nextUser, nextProject), snapshot: nextSnapshot };
      if (Object.keys(nextSnapshot.openFloatingWindows).length > 0) {
        restoringFloatingWindowsUntil = Date.now() + 1800;
        useFloatingWindowStore.getState().restoreWorkspaceState({ windows: nextSnapshot.openFloatingWindows });
      }
      if (nextSnapshot.ai.sessionId || nextSnapshot.ai.messages.length > 0) {
        const ai = useR2ChatStore.getState();
        if (nextSnapshot.ai.sessionId) ai.setSessionId(nextSnapshot.ai.sessionId);
        if (nextSnapshot.ai.context) ai.updateContext(nextSnapshot.ai.context);
        if (nextSnapshot.ai.contextSummary) ai.setContextSummary(nextSnapshot.ai.contextSummary);
      }
      notify();
      return nextSnapshot;
    },
    setActiveProject(projectIdValue) {
      update((s) => ({ ...s, lastActiveProjectId: projectIdValue, projectId: projectIdValue || 'workspace' }));
    },
    recordRoute(route) {
      if (!route) return;
      update((s) => ({ ...s, lastActiveRoute: route, navigationHistory: [route, ...s.navigationHistory.filter((r) => r !== route)].slice(0, 50) }));
    },
    recordDocument(doc) {
      update((s) => ({
        ...s,
        lastActiveDocumentId: doc.id,
        lastActiveDocumentName: doc.name ?? s.lastActiveDocumentName,
        lastActiveDrawingId: doc.drawingId ?? s.lastActiveDrawingId,
        lastActiveSheetId: doc.sheetId ?? s.lastActiveSheetId,
        recentFiles: [{ id: doc.id, name: doc.name || doc.id, path: doc.path ?? undefined, type: doc.type, openedAt: new Date().toISOString() }, ...s.recentFiles.filter((f) => f.id !== doc.id)].slice(0, 30),
      }));
    },
    recordTab(tabId) { update((s) => ({ ...s, lastSelectedTab: tabId })); },
    setPanelActive(panelId, active) { update((s) => ({ ...s, activePanels: active ? [panelId, ...s.activePanels.filter((p) => p !== panelId)] : s.activePanels.filter((p) => p !== panelId) })); },
    recordViewport(documentId, viewport) { if (documentId) update((s) => ({ ...s, viewportByDocument: { ...s.viewportByDocument, [documentId]: { ...(s.viewportByDocument[documentId] ?? {}), ...viewport } } })); },
    recordFilter(scope, value) { update((s) => ({ ...s, activeFilters: { ...s.activeFilters, [scope]: value } })); },
    recordSearch(query) { const q = query.trim(); if (q) update((s) => ({ ...s, searchHistory: [q, ...s.searchHistory.filter((x) => x !== q)].slice(0, 30) })); },
    recordSelectedObject(obj) { update((s) => ({ ...s, selectedObjects: obj ? [obj, ...s.selectedObjects].slice(0, 50) : [] })); },
    setTreeNodeExpanded(nodeId, expanded) { update((s) => ({ ...s, expandedTreeNodes: expanded ? [nodeId, ...s.expandedTreeNodes.filter((n) => n !== nodeId)] : s.expandedTreeNodes.filter((n) => n !== nodeId) })); },
    recordScroll(scope, scrollTop) { update((s) => ({ ...s, scrollPositions: { ...s.scrollPositions, [scope]: scrollTop } })); },
    recordUndoRedoSession(session) { update((s) => ({ ...s, undoRedoSession: session })); },
    recordAiWorkspace(workspace) { update((s) => ({ ...s, ai: { ...s.ai, activeWorkspace: workspace } })); },
    recordAiPrompt(prompt) { const p = prompt.trim(); if (p) update((s) => ({ ...s, ai: { ...s.ai, prompts: [p, ...s.ai.prompts.filter((x) => x !== p)].slice(0, 50) } })); },
    recordAiReference(reference) { update((s) => ({ ...s, ai: { ...s.ai, references: [reference, ...s.ai.references].slice(0, 50) } })); },
    syncFloatingWindows() {
      if (Date.now() < restoringFloatingWindowsUntil) return;
      const windows = useFloatingWindowStore.getState().windows;
      if (Object.keys(windows).length === 0 && Object.keys(state.snapshot.openFloatingWindows).length > 0) return;
      update((s) => ({ ...s, openFloatingWindows: windows, activePanels: Object.values(windows).filter((w) => w.open && !w.minimized).map((w) => w.id) }));
    },
    syncAiContext() {
      const ai = useR2ChatStore.getState();
      update((s) => ({ ...s, ai: { ...s.ai, sessionId: ai.sessionId, context: ai.context, contextSummary: ai.contextSummary, messages: ai.messages.slice(-80), activeWorkspace: ai.isOpen ? 'r2-ai-assistant' : s.ai.activeWorkspace } }));
    },
    flush() {
      if (saveTimer) window.clearTimeout(saveTimer);
      writeSnapshot(state.snapshot);
      if (state.snapshot.lastActiveProjectId) void saveProjectSettings(state.snapshot.lastActiveProjectId, { projectMemory: state.snapshot }).catch(() => undefined);
    },
    getProjectNumberMemory() {
      return state.snapshot.lastActiveProjectId || projectContextStore.getState().activeProjectId;
    },
  };
}

state = makeState();

export const projectMemoryStore = {
  getState: (): ProjectMemoryState => state,
  subscribe(fn: () => void): () => void { subs.add(fn); return () => subs.delete(fn); },
};

export function useProjectMemory(): ProjectMemoryState {
  return useSyncExternalStore(projectMemoryStore.subscribe, projectMemoryStore.getState, projectMemoryStore.getState);
}

export function installProjectMemoryAutosave(): () => void {
  const unsubWindows = useFloatingWindowStore.subscribe(() => projectMemoryStore.getState().syncFloatingWindows());
  const unsubAi = useR2ChatStore.subscribe(() => projectMemoryStore.getState().syncAiContext());
  const onProject = (e: Event) => {
    const projectId = (e as CustomEvent<{ projectId?: string | null }>).detail?.projectId ?? projectContextStore.getState().activeProjectId;
    projectMemoryStore.getState().hydrate(currentUserId(), projectId || 'workspace');
    projectMemoryStore.getState().setActiveProject(projectId ?? null);
  };
  const onBeforeUnload = () => projectMemoryStore.getState().flush();
  const onVisibility = () => { if (document.visibilityState === 'hidden') projectMemoryStore.getState().flush(); };
  const onAiPrompt = (e: Event) => {
    const prompt = (e as CustomEvent<{ prompt?: string }>).detail?.prompt;
    if (prompt) projectMemoryStore.getState().recordAiPrompt(prompt);
  };
  const onClick = (e: MouseEvent) => {
    const target = e.target;
    if (!(target instanceof Element)) return;
    const tab = target.closest('[role="tab"], [data-tab-id], [data-testid*="tab"], button[aria-selected]') as HTMLElement | null;
    const tabId = tab?.getAttribute('data-tab-id') || tab?.getAttribute('data-testid') || tab?.textContent?.trim().slice(0, 80);
    if (tabId) projectMemoryStore.getState().recordTab(tabId);
    const tree = target.closest('[aria-expanded][data-testid], [data-tree-node-id]') as HTMLElement | null;
    const nodeId = tree?.getAttribute('data-tree-node-id') || tree?.getAttribute('data-testid');
    const expanded = tree?.getAttribute('aria-expanded');
    if (nodeId && expanded !== null) projectMemoryStore.getState().setTreeNodeExpanded(nodeId, expanded === 'true');
  };
  const onInput = (e: Event) => {
    const target = e.target;
    if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLTextAreaElement)) return;
    const testId = target.getAttribute('data-testid') || '';
    const type = (target.getAttribute('type') || '').toLowerCase();
    const looksSearch = type === 'search' || /search|filter|query/i.test(testId) || /search|filter/i.test(target.placeholder || '');
    if (looksSearch && target.value.trim().length >= 2) projectMemoryStore.getState().recordSearch(target.value);
  };
  const onScroll = (e: Event) => {
    const target = e.target;
    if (!(target instanceof HTMLElement) || target === document.documentElement || target === document.body) return;
    const id = target.getAttribute('data-testid') || target.id;
    if (id && target.scrollTop > 0) projectMemoryStore.getState().recordScroll(id, target.scrollTop);
  };
  window.addEventListener('r2:project-active-changed', onProject);
  window.addEventListener('beforeunload', onBeforeUnload);
  window.addEventListener('r2:project-memory:ai-prompt', onAiPrompt);
  document.addEventListener('click', onClick, true);
  document.addEventListener('input', onInput, true);
  document.addEventListener('scroll', onScroll, true);
  document.addEventListener('visibilitychange', onVisibility);
  projectMemoryStore.getState().hydrate(currentUserId(), projectContextStore.getState().activeProjectId || 'workspace');
  return () => {
    unsubWindows();
    unsubAi();
    window.removeEventListener('r2:project-active-changed', onProject);
    window.removeEventListener('beforeunload', onBeforeUnload);
    window.removeEventListener('r2:project-memory:ai-prompt', onAiPrompt);
    document.removeEventListener('click', onClick, true);
    document.removeEventListener('input', onInput, true);
    document.removeEventListener('scroll', onScroll, true);
    document.removeEventListener('visibilitychange', onVisibility);
  };
}

export function readLastProjectMemory(): { userId: string; projectId: string; savedAt?: string } | null {
  if (!canUseStorage()) return null;
  try { return JSON.parse(localStorage.getItem(GLOBAL_LAST_KEY) || 'null') as { userId: string; projectId: string; savedAt?: string } | null; } catch { return null; }
}

export function rememberActiveDocument(doc: { id: string; name?: string | null; type?: string; path?: string | null; drawingId?: string | null; sheetId?: string | null }): void {
  projectMemoryStore.getState().recordDocument(doc);
}

export function rememberDrawingViewport(documentId: string, viewport: ProjectMemoryViewport): void {
  projectMemoryStore.getState().recordViewport(documentId, viewport);
}

export function getProjectMemorySnapshot(): ProjectMemorySnapshot {
  return projectMemoryStore.getState().snapshot;
}
